import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, ArrowRight, Check, Sparkles } from 'lucide-react';

/**
 * Customer Savings modal — opened from the homepage hero gold card.
 * Communicates ONE simple idea in under 3 seconds: food can cost less on
 * NoFee because participating restaurants don't pay marketplace fees.
 *
 * Modal structure (per product spec):
 *   Title: "Why can food cost less on NoFee?"
 *   Intro: two short paragraphs (no marketing fluff)
 *   ✓ 4 bullets ("Everyone wins")
 *   Primary CTA: Create Free Account → /customer/signup
 *   Secondary: Maybe Later (just closes the modal)
 */
const POINTS = [
  'Customers pay less',
  'Restaurants keep more profit',
  'Orders go directly to local restaurants',
  'No hidden marketplace markups',
];

const CustomerBenefitsModal = ({ open, onClose }) => {
  const navigate = useNavigate();

  // Close on ESC + lock background scroll while open.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  if (!open) return null;

  const go = (path) => {
    onClose?.();
    setTimeout(() => navigate(path), 30);
  };

  return (
    <div
      className="fixed inset-0 z-[9999] bg-black/75 backdrop-blur-sm flex items-center justify-center p-4"
      style={{ width: '100vw', height: '100dvh' }}
      onClick={onClose}
      data-testid="customer-benefits-modal"
    >
      <div
        className="relative w-full sm:max-w-lg bg-white rounded-3xl shadow-2xl overflow-hidden flex flex-col"
        style={{ maxHeight: 'calc(100dvh - 32px)' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header with gold gradient — kept identical visual style */}
        <div className="relative bg-gradient-to-br from-amber-300 via-gold-400 to-amber-500 text-black p-6 sm:p-7 flex-shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="absolute top-3 right-3 w-9 h-9 rounded-full bg-black/15 hover:bg-black/25 flex items-center justify-center transition-colors"
            aria-label="Close"
            data-testid="customer-benefits-close"
          >
            <X className="w-5 h-5 text-black" />
          </button>
          <div className="inline-flex items-center gap-1.5 text-[11px] font-black uppercase tracking-[0.18em] text-black/70 mb-2">
            <Sparkles className="w-3.5 h-3.5" /> Customer Savings
          </div>
          <h2
            className="text-[26px] sm:text-[30px] font-black leading-[1.1] tracking-tight"
            data-testid="customer-benefits-title"
          >
            Why can food cost less on NoFee?
          </h2>
        </div>

        {/* Body — copy + bullets. Only the body scrolls; header+CTA stay pinned. */}
        <div className="px-6 sm:px-7 py-5 sm:py-6 overflow-y-auto flex-1 min-h-0">
          <p className="text-[14.5px] text-slate-700 leading-relaxed">
            Many restaurants can offer better prices through NoFee because they don't pay
            the high fees charged by traditional delivery apps.
          </p>
          <p className="mt-3 text-[14.5px] text-slate-700 leading-relaxed">
            This allows restaurants to keep more profit while offering better prices to customers.
          </p>

          <p className="mt-5 text-[11px] font-extrabold uppercase tracking-[0.18em] text-gold-600">
            Everyone wins:
          </p>
          <ul className="mt-2.5 space-y-2">
            {POINTS.map((p, i) => (
              <li
                key={i}
                className="flex items-start gap-2.5 text-[14.5px] text-slate-800"
                data-testid={`customer-benefit-${i}`}
              >
                <span className="w-5 h-5 rounded-md bg-gold-50 border border-gold-200 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Check className="w-3 h-3 text-gold-600" />
                </span>
                <span className="leading-snug">{p}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* CTAs */}
        <div className="px-6 sm:px-7 pb-6 sm:pb-7 border-t border-slate-100 pt-4 bg-white flex-shrink-0">
          <div className="flex flex-col sm:flex-row gap-2.5">
            <button
              type="button"
              onClick={() => go('/customer/signup')}
              data-testid="customer-benefits-create"
              className="flex-[2] inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-2xl bg-black hover:bg-neutral-800 text-white font-bold transition-colors"
            >
              Create Free Account <ArrowRight className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={onClose}
              data-testid="customer-benefits-later"
              className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-2xl border border-slate-300 hover:border-slate-400 hover:bg-slate-50 text-slate-700 font-bold transition-colors"
            >
              Maybe Later
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerBenefitsModal;
